import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../lib/firebase";

const TEMPLATE_TYPES = [
  "Kit/Bag",
  "Component",
  "Equipment",
  "Function",
];

const ASSIGNMENT_LOCATIONS = [
  "Cart 3",
  "Cart 5",
  "Cart 9",
  "Cart 12",
  "Cart 15",
  "Cart 17",
  "MD Cart",
  "Stretcher Cart 3",

  "Bike 1",
  "Bike 2",
  "Bike 3",

  "Supervisor 1",
  "Supervisor 2",

  "Command 1",
  "Command 2",
  "Command 3",
  
  "Tent 3",
  "Tent 5",
  "Tent 9",
  "Tent 12",
  "Tent 17",

  "HQ",
];

function locationSlug(name) {
  return String(name || "").toLowerCase().replace(/\s+/g, "");
}

function locationNameToType(name) {
  const n = String(name || "").toLowerCase();

  if (n.includes("cart")) return "cart";
  if (n.includes("bike")) return "bike";
  if (n.includes("tent")) return "tent";
  if (n.includes("hq")) return "hq";
  if (n.includes("supervisor")) return "supervisor";
  if (n.includes("command")) return "command";

  return "other";
}

export default function KitBuilderPage({ stockItems = [], styles }) {
  const [kits, setKits] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [locationViewOpen, setLocationViewOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [selectedKitId, setSelectedKitId] = useState("");
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const [addKitOpen, setAddKitOpen] = useState(false);
  const [editKitOpen, setEditKitOpen] = useState(false);
  const [cloneKitOpen, setCloneKitOpen] = useState(false);
  const [addLocationOpen, setAddLocationOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);

  useEffect(() => {
    loadKits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadKits() {
    try {
      setLoading(true);

      const [kitSnap, assignmentSnap] = await Promise.all([
        getDocs(collection(db, "inventoryKitTemplates")),
        getDocs(collection(db, "inventoryKitAssignments")),
      ]);

      const kitData = kitSnap.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter((kit) => kit.active !== false)
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

      const assignmentData = assignmentSnap.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter((assignment) => assignment.active !== false);

      setKits(kitData);
      setAssignments(assignmentData);

      setSelectedKitId((current) => {
        if (current && kitData.some((kit) => kit.id === current)) return current;
        return kitData[0]?.id || "";
      });
    } catch (err) {
      console.error("Failed to load kit templates:", err);
      alert("Failed to load kit templates.");
    } finally {
      setLoading(false);
    }
  }

  const selectedKit = kits.find((kit) => kit.id === selectedKitId);

  const selectedAssignments = assignments.filter(
    (assignment) => assignment.templateId === selectedKitId
  );

  const filteredKits = useMemo(() => {
    return kits.filter((kit) => {
      const q = search.toLowerCase();

      const matchesSearch =
        String(kit.name || "").toLowerCase().includes(q) ||
        String(kit.category || "").toLowerCase().includes(q);

     const matchesCategory =
  categoryFilter === "all" ||
  (kit.templateType || kit.category) === categoryFilter;

      return matchesSearch && matchesCategory;
    });
  }, [kits, search, categoryFilter]);

  async function updateSelectedKit(updates) {
    if (!selectedKit?.id) return;

    await updateDoc(doc(db, "inventoryKitTemplates", selectedKit.id), {
      ...updates,
      updatedAt: serverTimestamp(),
    });

    await loadKits();
  }

  async function handleDeleteKit() {
    if (!selectedKit) return;

    const confirmed = window.confirm(`Delete ${selectedKit.name}?`);
    if (!confirmed) return;

    await updateSelectedKit({ active: false });
    setSelectedKitId("");
  }

  async function handleRemoveAssignment(assignment) {
    const confirmed = window.confirm(
      `Remove ${assignment.locationName} assignment?`
    );

    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, "inventoryKitAssignments", assignment.id));

      await loadKits();
    } catch (err) {
      console.error("Failed to remove assignment:", err);
      alert("Failed to remove assignment.");
    }
  }

  if (loading) {
    return <div style={styles.panel}>Loading Kit Builder...</div>;
  }

  return (
    <div style={styles.panel}>
      <div style={styles.panelHeader}>
        <div>
          <h2 style={styles.panelTitle}>Kit Builder</h2>
          <p style={styles.subtitle}>
            Build kit templates, add compartments, assign templates to units,
            and set inventory PAR levels.
          </p>
        </div>

        <div style={{ display: "flex", gap: 10 }}>
  <button
    style={styles.secondaryButton}
    onClick={() => setLocationViewOpen(true)}
  >
    View Locations
  </button>

  <button style={styles.primaryButton} onClick={() => setAddKitOpen(true)}>
    + Add Kit
  </button>
</div>
      </div>

      <div style={styles.filterBar}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search kits..."
          style={styles.searchInput}
        />

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="all">All Kit Types</option>
          {TEMPLATE_TYPES.map((type) => (
           <option key={type} value={type}>
            {type}
          </option>
          ))}
        </select>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "280px minmax(0, 1fr)",
          gap: 18,
          alignItems: "start",
        }}
      >
        <div
          style={{
            border: "1px solid #e2e8f0",
            borderRadius: 16,
            overflow: "hidden",
            background: "#f8fafc",
          }}
        >
          {filteredKits.map((kit) => (
            <button
              key={kit.id}
              onClick={() => setSelectedKitId(kit.id)}
              style={{
                width: "100%",
                textAlign: "left",
                padding: 14,
                border: "none",
                borderBottom: "1px solid #e2e8f0",
                background: kit.id === selectedKitId ? "#dbeafe" : "#fff",
                cursor: "pointer",
              }}
            >
              <strong>{kit.name}</strong>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                {kit.templateType || kit.category || "Uncategorized"} ·{" "}
                {(kit.locations || []).length} location(s)
              </div>
            </button>
          ))}

          {filteredKits.length === 0 && (
            <div style={{ padding: 16, color: "#64748b" }}>No kits found.</div>
          )}
        </div>

        {!selectedKit ? (
          <div style={styles.placeholderBox}>Select or add a kit to begin.</div>
        ) : (
          <div>
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 18,
                padding: 18,
                background: "#fff",
                marginBottom: 16,
              }}
            >
              <div style={styles.panelHeader}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 24 }}>
                    {selectedKit.name}
                  </h3>
                  <p style={styles.subtitle}>{selectedKit.templateType || selectedKit.category}</p>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button
                    style={styles.secondaryButton}
                    onClick={() => setAddLocationOpen(true)}
                  >
                    + Add Location
                  </button>

                  <button
                    style={styles.secondaryButton}
                    onClick={() => setEditKitOpen(true)}
                  >
                    Edit Template
                  </button>

                  <button
                    style={styles.secondaryButton}
                    onClick={() => setCloneKitOpen(true)}
                  >
                    Clone Template
                  </button>

                  <button
                    style={styles.secondaryButton}
                    onClick={() => setAssignModalOpen(true)}
                  >
                    Assign Template
                  </button>

                  <button
                    style={styles.smallDangerButton}
                    onClick={handleDeleteKit}
                  >
                    Delete Kit
                  </button>
                </div>
              </div>

              <div
                style={{
                  marginTop: 18,
                  borderTop: "1px solid #e2e8f0",
                  paddingTop: 18,
                }}
              >
                <h4 style={{ marginTop: 0 }}>Assigned Locations</h4>

                {selectedAssignments.length === 0 && (
                  <div style={{ color: "#64748b" }}>
                    No locations assigned yet.
                  </div>
                )}

                <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                  {selectedAssignments.map((assignment) => (
                    <div
                      key={assignment.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        background: "#eff6ff",
                        border: "1px solid #bfdbfe",
                        padding: "8px 12px",
                        borderRadius: 999,
                      }}
                    >
                      <span>{assignment.locationName}</span>

                      <button
                        onClick={() => handleRemoveAssignment(assignment)}
                        style={{
                          border: "none",
                          background: "transparent",
                          color: "#dc2626",
                          cursor: "pointer",
                          fontWeight: 900,
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {(selectedKit.locations || []).map((location) => (
              <KitLocationCard
                key={location.id}
                kit={selectedKit}
                location={location}
                styles={styles}
                onAddItem={() => setAddItemOpen(location.id)}
                onUpdateKit={updateSelectedKit}
              />
            ))}

            {(selectedKit.locations || []).length === 0 && (
              <div style={styles.placeholderBox}>
                No locations yet. Add locations like Drawer 1, Left Pouch, Main
                Compartment, etc.
              </div>
            )}
          </div>
        )}
      </div>

      {addKitOpen && (
        <AddKitModal
          styles={styles}
          onClose={() => setAddKitOpen(false)}
          onSaved={async () => {
            setAddKitOpen(false);
            await loadKits();
          }}
        />
      )}

      {editKitOpen && selectedKit && (
        <EditKitModal
          styles={styles}
          kit={selectedKit}
          onClose={() => setEditKitOpen(false)}
          onSave={async ({ name, templateType }) => {
          await updateSelectedKit({
            name,
            templateType,
          });
          setEditKitOpen(false);
        }}
        />
      )}

      {cloneKitOpen && selectedKit && (
        <CloneKitModal
          styles={styles}
          kit={selectedKit}
          onClose={() => setCloneKitOpen(false)}
          onSaved={async () => {
            setCloneKitOpen(false);
            await loadKits();
          }}
        />
      )}

      {addLocationOpen && selectedKit && (
        <AddLocationModal
          styles={styles}
          onClose={() => setAddLocationOpen(false)}
          onSave={async (locationName) => {
            const newLocation = {
              id: `loc_${Date.now()}`,
              name: locationName,
              items: [],
            };

            await updateSelectedKit({
              locations: [...(selectedKit.locations || []), newLocation],
            });

            setAddLocationOpen(false);
          }}
        />
      )}

      {addItemOpen && selectedKit && (
        <AddKitItemModal
          styles={styles}
          stockItems={stockItems}
          availableKits={kits}
          currentKitId={selectedKit.id}
          onClose={() => setAddItemOpen(false)}
          onSave={async (newItem) => {
            const updatedLocations = (selectedKit.locations || []).map((loc) => {
              if (loc.id !== addItemOpen) return loc;

              return {
                ...loc,
                items: [...(loc.items || []), newItem],
              };
            });

            await updateSelectedKit({ locations: updatedLocations });
            setAddItemOpen(false);
          }}
        />
      )}
      
      {locationViewOpen && (
  <LocationAssignmentsModal
  styles={styles}
  assignments={assignments}
  kits={kits}
  onClose={() => setLocationViewOpen(false)}
/>
)}

      {assignModalOpen && selectedKit && (
        <AssignTemplateModal
          styles={styles}
          selectedKit={selectedKit}
          assignments={assignments}
          onClose={() => setAssignModalOpen(false)}
          onSaved={async () => {
            setAssignModalOpen(false);
            await loadKits();
          }}
        />
      )}
    </div>
  );
}

function KitLocationCard({ kit, location, styles, onAddItem, onUpdateKit }) {
  async function renameLocation() {
  const newName = window.prompt("Enter new location name:", location.name);

  if (!newName || !newName.trim()) return;

  const updatedLocations = (kit.locations || []).map((loc) =>
    loc.id === location.id
      ? { ...loc, name: newName.trim() }
      : loc
  );

  await onUpdateKit({ locations: updatedLocations });
}
  async function removeLocation() {
    const confirmed = window.confirm(`Remove ${location.name}?`);
    if (!confirmed) return;

    const updatedLocations = (kit.locations || []).filter(
      (loc) => loc.id !== location.id
    );

    await onUpdateKit({ locations: updatedLocations });
  }

  async function removeItem(itemId) {
    const updatedLocations = (kit.locations || []).map((loc) => {
      if (loc.id !== location.id) return loc;

      return {
        ...loc,
        items: (loc.items || []).filter((item) => item.id !== itemId),
      };
    });

    await onUpdateKit({ locations: updatedLocations });
  }

  async function updatePar(itemId, parLevel) {
    const updatedLocations = (kit.locations || []).map((loc) => {
      if (loc.id !== location.id) return loc;

      return {
        ...loc,
        items: (loc.items || []).map((item) =>
          item.id === itemId
            ? { ...item, parLevel: Number(parLevel || 0) }
            : item
        ),
      };
    });

    await onUpdateKit({ locations: updatedLocations });
  }
  
async function moveLocation(direction) {
  const locations = [...(kit.locations || [])];
  const index = locations.findIndex((loc) => loc.id === location.id);

  if (index === -1) return;

  const newIndex = direction === "up" ? index - 1 : index + 1;

  if (newIndex < 0 || newIndex >= locations.length) return;

  [locations[index], locations[newIndex]] = [
    locations[newIndex],
    locations[index],
  ];

  await onUpdateKit({ locations });
}

  async function moveItem(itemId, direction) {
  const updatedLocations = (kit.locations || []).map((loc) => {
    if (loc.id !== location.id) return loc;

    const items = [...(loc.items || [])];

    const index = items.findIndex((i) => i.id === itemId);

    if (index === -1) return loc;

    const newIndex =
      direction === "up" ? index - 1 : index + 1;

    if (newIndex < 0 || newIndex >= items.length) {
      return loc;
    }

    [items[index], items[newIndex]] = [
      items[newIndex],
      items[index],
    ];

    return {
      ...loc,
      items,
    };
  });

  await onUpdateKit({ locations: updatedLocations });
}

  return (
    <div
      style={{
        border: "1px solid #e2e8f0",
        borderRadius: 18,
        padding: 16,
        background: "#fff",
        marginBottom: 14,
      }}
    >
      <div style={styles.panelHeader}>
        <div>
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <h3 style={{ margin: 0 }}>{location.name}</h3>

    <button
      type="button"
      onClick={renameLocation}
      style={styles.secondaryButton}
    >
      Edit Label
    </button>

      <button
  type="button"
  onClick={() => moveLocation("up")}
  style={styles.secondaryButton}
>
  ↑
</button>

<button
  type="button"
  onClick={() => moveLocation("down")}
  style={styles.secondaryButton}
>
  ↓
</button>

  </div>

  <p style={styles.subtitle}>
    {(location.items || []).length} item(s)
  </p>
</div>

        <div style={{ display: "flex", gap: 8 }}>
          <button style={styles.secondaryButton} onClick={onAddItem}>
            + Add Item
          </button>

          <button style={styles.smallDangerButton} onClick={removeLocation}>
            Remove Location
          </button>
        </div>
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Item</th>
              <th style={styles.th}>Category</th>
              <th style={styles.th}>Unit</th>
              <th style={styles.th}>
                Qty / Check
              </th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>

          <tbody>
            {(location.items || []).map((item) => (
              <tr key={item.id}>
                <td style={styles.td}>
                  <strong>{item.itemName}</strong>

{item.type === "nestedKit" && (
  <div
    style={{
      fontSize: 11,
      color: "#2563eb",
      marginTop: 4,
      fontWeight: 600,
    }}
  >
    Component Kit
  </div>
)}
                </td>
                <td style={styles.td}>{item.category || "—"}</td>
                <td style={styles.td}>{item.unitType || "each"}</td>
                <td style={styles.td}>
  {item.type === "checkItem" ? (
    <div
      style={{
        fontWeight: 700,
        color: "#16a34a",
      }}
    >
      YES / NO
    </div>
  ) : (
    <input
      type="number"
      min="0"
      value={item.parLevel || 0}
      onChange={(e) => updatePar(item.id, e.target.value)}
      style={styles.qtyInput}
    />
  )}
</td>
                <td style={styles.td}>
  <div
    style={{
      display: "flex",
      gap: 6,
      flexWrap: "wrap",
    }}
  >
    <button
      style={styles.secondaryButton}
      onClick={() => moveItem(item.id, "up")}
    >
      ↑
    </button>

    <button
      style={styles.secondaryButton}
      onClick={() => moveItem(item.id, "down")}
    >
      ↓
    </button>

    <button
      style={styles.smallDangerButton}
      onClick={() => removeItem(item.id)}
    >
      Remove
    </button>
  </div>
</td>
              </tr>
            ))}

            {(location.items || []).length === 0 && (
              <tr>
                <td style={styles.td} colSpan="5">
                  No items in this location yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AddKitModal({ styles, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [templateType, setTemplateType] = useState("Kit/Bag");
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!name.trim()) {
      alert("Enter a kit name.");
      return;
    }

    try {
      setSaving(true);

      await addDoc(collection(db, "inventoryKitTemplates"), {
        name: name.trim(),
        templateType,
        locations: [],
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await onSaved();
    } catch (err) {
      console.error("Failed to add kit:", err);
      alert("Failed to add kit.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>Add Kit</h2>
            <p style={styles.subtitle}>Create a reusable kit template.</p>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        <label style={styles.fieldLabel}>Kit Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Cart Kit, MD Bag, Crash Cart, etc."
          style={styles.input}
        />

        <label style={styles.fieldLabel}>Template Type</label>
        <select
         value={templateType}
          onChange={(e) => setTemplateType(e.target.value)}
          style={styles.select}
        >
          {TEMPLATE_TYPES.map((type) => (
            <option key={type} value={type}>
            {type}
          </option>
          ))}
        </select>

        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.secondaryButton}>
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              ...styles.primaryButton,
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? "Saving..." : "Add Kit"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditKitModal({ styles, kit, onClose, onSave }) {
  const [name, setName] = useState(kit.name || "");
  const [templateType, setTemplateType] = useState(
  kit.templateType || kit.category || "Kit/Bag"
);

  function handleSave() {
    if (!name.trim()) {
      alert("Enter a kit/template name.");
      return;
    }

    onSave({
      name: name.trim(),
      templateType,
    });
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>Edit Kit Template</h2>
            <p style={styles.subtitle}>Rename or recategorize this template.</p>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        <label style={styles.fieldLabel}>Template Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={styles.input}
        />

        <label style={styles.fieldLabel}>Template Type</label>
        <select
          value={templateType}
          onChange={(e) => setTemplateType(e.target.value)}
          style={styles.select}
        >
          {TEMPLATE_TYPES.map((type) => (
            <option key={type} value={type}>
            {type}
          </option>
          ))}
        </select>

        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.secondaryButton}>
            Cancel
          </button>

          <button onClick={handleSave} style={styles.primaryButton}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
}

function CloneKitModal({ styles, kit, onClose, onSaved }) {
  const [name, setName] = useState(`${kit.name || "Kit"} Copy`);
 const [templateType, setTemplateType] = useState(
  kit.templateType || kit.category || "Kit/Bag"
);
  const [saving, setSaving] = useState(false);

  async function handleClone() {
    if (!name.trim()) {
      alert("Enter a new kit/template name.");
      return;
    }

    try {
      setSaving(true);

      await addDoc(collection(db, "inventoryKitTemplates"), {
        name: name.trim(),
        templateType,
        locations: JSON.parse(JSON.stringify(kit.locations || [])),
        active: true,
        clonedFromTemplateId: kit.id,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await onSaved();
    } catch (err) {
      console.error("Failed to clone kit:", err);
      alert("Failed to clone kit.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>Clone Kit Template</h2>
            <p style={styles.subtitle}>Creates a new editable copy.</p>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        <label style={styles.fieldLabel}>New Template Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={styles.input}
        />

        <label style={styles.fieldLabel}>Template Type</label>
        <select
          value={templateType}
          onChange={(e) => setTemplateType(e.target.value)}
          style={styles.select}
        >
          {TEMPLATE_TYPES.map((type) => (
            <option key={type} value={type}>
            {type}
          </option>
          ))}
        </select>

        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.secondaryButton}>
            Cancel
          </button>

          <button
            onClick={handleClone}
            disabled={saving}
            style={{
              ...styles.primaryButton,
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? "Cloning..." : "Clone Template"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddLocationModal({ styles, onClose, onSave }) {
  const [name, setName] = useState("");

  function handleSave() {
    if (!name.trim()) {
      alert("Enter a location name.");
      return;
    }

    onSave(name.trim());
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>Add Kit Location</h2>
            <p style={styles.subtitle}>
              Examples: Drawer 1, Drawer 2, Left Pouch, Main Compartment.
            </p>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        <label style={styles.fieldLabel}>Location Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={styles.input}
        />

        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.secondaryButton}>
            Cancel
          </button>

          <button onClick={handleSave} style={styles.primaryButton}>
            Add Location
          </button>
        </div>
      </div>
    </div>
  );
}

function AddKitItemModal({
  styles,
  stockItems,
  availableKits,
  currentKitId,
  onClose,
  onSave,
}) {
  const [selectedStockId, setSelectedStockId] = useState("");
  const [itemType, setItemType] = useState("stock");
  const [selectedKitId, setSelectedKitId] = useState("");
  const [parLevel, setParLevel] = useState(1);
  const [checkItemName, setCheckItemName] = useState("");
  const [checkPreset, setCheckPreset] = useState("Yes/No");
  const [customPositive, setCustomPositive] = useState("Yes");
  const [customNegative, setCustomNegative] = useState("No");

const filteredStockItems = useMemo(() => {
  const map = {};

  stockItems
    .filter((item) => item.active !== false)
    .forEach((item) => {
      const key = [
        String(item.itemName || "").trim().toLowerCase(),
        String(item.supplier || "").trim().toLowerCase(),
        String(item.itemNumber || item.vendorPartNumber || "").trim().toLowerCase(),
      ].join("|");

      if (!map[key]) {
        map[key] = item;
      }
    });

  return Object.values(map).sort((a, b) =>
    String(a.itemName || "").localeCompare(String(b.itemName || ""))
  );
}, [stockItems]);

 const selectedStock = stockItems.find((item) => item.id === selectedStockId); 

 function getCheckOptions() {
  if (checkPreset === "Yes/No") return ["Yes", "No"];
  if (checkPreset === "Successful/Unsuccessful") return ["Successful", "Unsuccessful"];
  if (checkPreset === "Pass/Fail") return ["Pass", "Fail"];
  if (checkPreset === "Present/Missing") return ["Present", "Missing"];
  if (checkPreset === "On/Off") return ["On", "Off"];

  return [
    customPositive.trim() || "Yes",
    customNegative.trim() || "No",
  ];
}

 function handleSave() {
  if (itemType === "stock") {
    if (!selectedStock) {
      alert("Select an item.");
      return;
    }

    onSave({
      id: `kitItem_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      type: "stock",
      inventoryStockId: selectedStock.id,
      itemName: selectedStock.itemName || "",
      category: selectedStock.category || "",
      unitType: selectedStock.unitType || "each",
      supplier: selectedStock.supplier || "",
      itemNumber: selectedStock.itemNumber || "",
      vendorPartNumber: selectedStock.vendorPartNumber || "",
      parLevel: Number(parLevel || 0),
    });

    return;
  }

  if (itemType === "checkItem") {
  if (!checkItemName.trim()) {
    alert("Enter a checklist item.");
    return;
  }

  onSave({
    id: `checkItem_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 9)}`,
    type: "checkItem",
    itemName: checkItemName.trim(),
    category: "Checklist",
    unitType: "check",
    parLevel: 0,
    checkOptions: getCheckOptions(),
  });

  return;
}

  const nestedKit = availableKits.find((k) => k.id === selectedKitId);

  if (!nestedKit) {
    alert("Select a kit.");
    return;
  }

  onSave({
    id: `nestedKit_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    type: "nestedKit",
    nestedTemplateId: nestedKit.id,
    nestedTemplateName: nestedKit.name,
    itemName: nestedKit.name,
    category: "Nested Kit",
    unitType: "kit",
    parLevel: Number(parLevel || 1),
  });
}

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>Add Item to Kit</h2>
            <p style={styles.subtitle}>
              Pull an item from Inventory Management and set its kit PAR level.
            </p>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        <label style={styles.fieldLabel}>Item Type</label>

<select
  value={itemType}
  onChange={(e) => setItemType(e.target.value)}
  style={styles.select}
>
  <option value="stock">Supply Item</option>
  <option value="nestedKit">Component Kit</option>
  <option value="checkItem">Checklist Item</option>
  </select>

{itemType === "stock" && (
  <>
    <label style={styles.fieldLabel}>Item</label>

    <select
      value={selectedStockId}
      onChange={(e) => setSelectedStockId(e.target.value)}
      style={styles.select}
    >
      <option value="">Select Item</option>

      {filteredStockItems.map((item) => (
        <option key={item.id} value={item.id}>
          {item.itemName} — {item.category || "Uncategorized"}
        </option>
      ))}
    </select>
  </>
)}

{itemType === "nestedKit" && (
  <>
    <label style={styles.fieldLabel}>Component Kit</label>

    <select
      value={selectedKitId}
      onChange={(e) => setSelectedKitId(e.target.value)}
      style={styles.select}
    >
      <option value="">Select Kit</option>

      {availableKits
        .filter((kit) => kit.id !== currentKitId)
        .filter(
          (kit) =>
            kit.templateType === "Component"
        )
        .map((kit) => (
          <option key={kit.id} value={kit.id}>
            {kit.name}
          </option>
        ))}
    </select>
  </>
)}

{itemType === "checkItem" && (
  <>
    <label style={styles.fieldLabel}>Checklist Item</label>

    <input
      value={checkItemName}
      onChange={(e) => setCheckItemName(e.target.value)}
      placeholder="Power On, Battery Installed, Pads Connected..."
      style={styles.input}
    />

    <label style={styles.fieldLabel}>Button Labels</label>

    <select
      value={checkPreset}
      onChange={(e) => setCheckPreset(e.target.value)}
      style={styles.select}
    >
      <option value="Yes/No">Yes / No</option>
      <option value="Successful/Unsuccessful">Successful / Unsuccessful</option>
      <option value="Pass/Fail">Pass / Fail</option>
      <option value="Present/Missing">Present / Missing</option>
      <option value="On/Off">On / Off</option>
      <option value="Custom">Custom</option>
    </select>

    {checkPreset === "Custom" && (
      <>
        <label style={styles.fieldLabel}>Positive Button</label>
        <input
          value={customPositive}
          onChange={(e) => setCustomPositive(e.target.value)}
          style={styles.input}
        />

        <label style={styles.fieldLabel}>Negative Button</label>
        <input
          value={customNegative}
          onChange={(e) => setCustomNegative(e.target.value)}
          style={styles.input}
        />
      </>
    )}
  </>
)}

        {itemType !== "checkItem" && (
  <>
    <label style={styles.fieldLabel}>PAR Level</label>

    <input
      type="number"
      min="0"
      value={parLevel}
      onChange={(e) => setParLevel(e.target.value)}
      style={styles.input}
    />
  </>
)}

        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.secondaryButton}>
            Cancel
          </button>

          <button onClick={handleSave} style={styles.primaryButton}>
            Add Item
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignTemplateModal({
  styles,
  selectedKit,
  assignments,
  onClose,
  onSaved,
}) {
  const [locationName, setLocationName] = useState("");
  const [saving, setSaving] = useState(false);

  const availableLocations = ASSIGNMENT_LOCATIONS;

  async function handleAssign() {
    if (!locationName) {
      alert("Select a location.");
      return;
    }

    const existing = assignments.find(
      (assignment) =>
        assignment.active !== false &&
        assignment.locationName === locationName &&
        assignment.templateId === selectedKit.id
    );

    if (existing) {
      alert("This exact kit is already assigned to that location.");
      return;
    }

    try {
      setSaving(true);

      const slug = locationSlug(locationName);
      const locationType = locationNameToType(locationName);

      await addDoc(collection(db, "inventoryKitAssignments"), {
        templateId: selectedKit.id,
        templateName: selectedKit.name,
        templateType: selectedKit.templateType || "Kit/Bag",
        locationName,
        locationSlug: slug,
        active: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      await setDoc(
        doc(db, "inventoryLocations", slug),
        {
          name: locationName,
          slug,
          type: locationType,
          active: true,
          inventoryType: "kit",
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      await onSaved();
    } catch (err) {
      console.error("Failed to assign template:", err);
      alert("Failed to assign template.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={styles.modalOverlay}>
      <div style={styles.modal}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>Assign Template</h2>
            <p style={styles.subtitle}>
              Assign {selectedKit.name} to a responder unit or inventory location.
            </p>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        <label style={styles.fieldLabel}>Assignment Location</label>

        <select
          value={locationName}
          onChange={(e) => setLocationName(e.target.value)}
          style={styles.select}
        >
          <option value="">Select Location</option>

          {availableLocations.map((location) => (
            <option key={location} value={location}>
              {location}
            </option>
          ))}
        </select>

        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.secondaryButton}>
            Cancel
          </button>

          <button
            type="button"
            onClick={handleAssign}
            disabled={saving}
            style={{
              ...styles.primaryButton,
              opacity: saving ? 0.6 : 1,
            }}
          >
            {saving ? "Assigning..." : "Assign Template"}
          </button>
        </div>
      </div>
    </div>
  );
}
function LocationAssignmentsModal({ styles, assignments, kits, onClose }) {
  const validTemplateIds = new Set(kits.map((kit) => kit.id));

const activeAssignments = assignments.filter(
  (a) => a.active !== false && validTemplateIds.has(a.templateId)
);

  const grouped = activeAssignments.reduce((acc, assignment) => {
    const location = assignment.locationName || "Unknown Location";

    if (!acc[location]) acc[location] = [];
    acc[location].push(assignment);

    return acc;
  }, {});

  const locations = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  return (
    <div style={styles.modalOverlay}>
      <div style={{ ...styles.modal, maxWidth: 800 }}>
        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>Assigned Kits by Location</h2>
            <p style={styles.subtitle}>
              View each responder unit or inventory location and the kits assigned to it.
            </p>
          </div>

          <button onClick={onClose} style={styles.closeButton}>
            Close
          </button>
        </div>

        {locations.length === 0 && (
          <div style={styles.placeholderBox}>
            No kit assignments found yet.
          </div>
        )}

        <div style={{ display: "grid", gap: 12 }}>
          {locations.map((location) => (
            <div
              key={location}
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: 14,
                padding: 14,
                background: "#f8fafc",
              }}
            >
              <h3 style={{ margin: "0 0 10px" }}>{location}</h3>

              <div style={{ display: "grid", gap: 8 }}>
                {grouped[location].map((assignment) => (
                  <div
                    key={assignment.id}
                    style={{
                      background: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: 12,
                      padding: "10px 12px",
                    }}
                  >
                    <strong>{assignment.templateName}</strong>
                    <div style={{ fontSize: 13, color: "#64748b" }}>
                      {assignment.templateType || "Kit/Bag"}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div style={styles.modalActions}>
          <button onClick={onClose} style={styles.primaryButton}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}